<template>
  <div class="products">
    <GridPanelHeader
      title=""
      :grid-ref="$refs.contractProductGrid"
      :show-add="true"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
      @add="handleAddClick"
    />
    <DxDataGrid
      :data-source="localData.products"
      :show-borders="true"
      :show-column-lines="true"
      :column-auto-width="false"
      :hover-state-enabled="true"
      :repaint-changes-only="true"
      :row-alternation-enabled="true"
      ref="contractProductGrid"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      @row-updated="onRowUpdated"
      @cell-prepared="onCellPrepared"
      key-expr="uuid"
    >
      <DxColumn dataField="name" :caption="$t('common.product')" :allowEditing="false">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn dataField="code" :caption="$t('stock_management.products.code')" :allowEditing="false"> </DxColumn>
      <DxColumn
        dataField="quantity"
        :caption="$t('stock_management.contracts_purchase_sale.contracted_quantity')"
        data-type="number"
        :allowEditing="true"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
        <DxCustomRule :validationCallback="(e) => e.value >= 1" :message="$t('validation.min_value', { min: 1 })" />
      </DxColumn>
      <DxColumn
        dataField="unit_price"
        :caption="$t('common.unit_price')"
        data-type="number"
        :allowEditing="true"
      />
      <DxColumn
        dataField="discount"
        :caption="$t('stock_management.invoice_management.discount') + '(%)'"
        data-type="number"
        :allowEditing="true"
      >
        <DxCustomRule
          :validationCallback="(e) => e.value === null || e.value === undefined || e.value === '' || (e.value >= 0 && e.value <= 100)"
          :message="$t('validation.value_between', { min: 0, max: 100 })"
        />
      </DxColumn>
      <DxColumn
        dataField="unit_price_discounted"
        :caption="$t('stock_management.invoice_management.unit_price_discounted')"
        data-type="number"
        :allowEditing="false"
      />
      <DxColumn
        dataField="price_netto"
        :caption="$t('stock_management.invoice_management.price_netto')"
        data-type="number"
        :allowEditing="true"
      />
      <DxColumn
        dataField="product_name_override"
        :caption="$t('stock_management.contracts_purchase_sale.product_name_override')"
        data-type="string"
        :allowEditing="true"
      />
      <DxColumn
        dataField="measure_unit"
        :caption="$t('common.measure_unit')"
        :allowEditing="false"
      >
      </DxColumn>
      <DxEditing mode="cell" :allowUpdating="true" :allowAdding="false" :allowDeleting="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: this.$t('common.product'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :save-row-changes="$t('common.save')"
          :cancel-row-changes="$t('common.close')"
          :addRow="$t('common.add_new')"
          :editRow="$t('common.edit')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxHeaderFilter :visible="false" />
      <DxFilterRow :visible="false" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
    </DxDataGrid>
    <ProductSelector
      ref="productSelector"
      :visible="showProductDialog"
      @close="showProductDialog = false"
      @continue="onContinueFromProductSelector"
      :multiselect="true"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { getQuantityFormatter } from "~/helpers/stockHelper";
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxRequiredRule,
  DxCustomRule,
  DxToolbar,
  DxItem,
} from "devextreme-vue/data-grid";
import ProductSelector from "@/components/ProductSelector.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "products",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid contract-product-grid",
      },
      localData: { ...this.formData },
      showProductDialog: false,
      editedDataField: null,
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    quantityFormatter() {
      return getQuantityFormatter("Kg");
    },
  },
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxRequiredRule,
    DxCustomRule,
    DxToolbar,
    DxItem,
    ProductSelector,
    GridPanelHeader,
  },
  methods: {
    ...mapActions({}),
    validate() {
      this.$refs.contractProductGrid.instance.saveEditData();

      if (!this.localData.products.length || this.localData.products.some((p) => !p.quantity || p.quantity === 0)) {
        return [this.$t("validation.products_and_quantity_required")];
      }

      return [];
    },
    updateData(newData) {
      if (JSON.stringify(this.getDataToEmit(this.localData)) !== JSON.stringify(newData)) {
        //console.log("updateData step 2", newData);
        this.localData = { ...newData }; // Update localData with new data
      }
    },
    getDataToEmit(data) {
      return {
        ...data,
        products: (data.products || []).map(({ price_netto, ...product }) => product),
      };
    },
    onEditingStart(e) {
      this.editedDataField = e.column.dataField || null;
    },
    onRowUpdated(e) {
      const row = e.data;
      const product = this.localData.products.find((item) => item.uuid === row.uuid);
      if (!product) return;

      const quantity = row.quantity !== undefined ? parseFloat(row.quantity) : parseFloat(product.quantity);
      const discount = row.discount !== undefined ? parseFloat(row.discount) : parseFloat(product.discount || 0);
      const unitPrice = row.unit_price !== undefined ? parseFloat(row.unit_price) : parseFloat(product.unit_price);
      const priceNetto = row.price_netto !== undefined ? parseFloat(row.price_netto) : parseFloat(product.price_netto);

      if (this.editedDataField === "price_netto" && Number.isFinite(priceNetto)) {
        product.unit_price_discounted = quantity > 0 ? (priceNetto / quantity).toFixed(3) : 0;
        product.discount = unitPrice > 0 ? (100 - (parseFloat(product.unit_price_discounted) / unitPrice) * 100).toFixed(2) : 0;
      } else if (Number.isFinite(unitPrice)) {
        product.unit_price_discounted = (unitPrice * (1 - discount / 100)).toFixed(3);
      }

      product.price_netto = Number.isFinite(quantity) && Number.isFinite(parseFloat(product.unit_price_discounted))
        ? (parseFloat(product.unit_price_discounted) * quantity).toFixed(2)
        : null;
    },
    onCellPrepared(e) {
      // Only for data rows and editable columns
      if (e.rowType === "data" && e.column.allowEditing) {
        e.cellElement.classList.add("editable-cell-yellow");
      }
    },
    handleAddClick(e) {
      this.showProductDialog = true; // Show the product selector dialog
    },
    onContinueFromProductSelector(selectedProducts) {
      selectedProducts.forEach((product) => {
        if (!this.localData.products.some((p) => p.product_uuid === product.uuid)) {
          this.localData.products.push({
            ...{
              uuid: product.uuid,
              product_uuid: product.uuid,
              name: product.name,
              quantity: 1,
              unit_price: null,
              discount: 0,
              unit_price_discounted: null,
              code: product.code,
              measure_unit: product.measure_unit.name,
              measure_unit_id: product.measure_unit.id,
            },
          });
        }
      });

      this.showProductDialog = false;
    },
  },
  watch: {
    formData: {
      deep: true,
      handler(newData) {
        if (JSON.stringify(this.getDataToEmit(this.localData)) !== JSON.stringify(newData)) {
          this.localData = { ...newData };
        }
      },
    },
    localData: {
      deep: true,
      handler(newData) {
        // Calculate unit_price_discounted for all products
        if (newData.products && Array.isArray(newData.products)) {
          newData.products.forEach((product) => {
            if (product.unit_price !== null && product.unit_price !== undefined) {
              const discount = product.discount || 0;
              product.unit_price_discounted = (product.unit_price * (1 - discount / 100)).toFixed(3);
              product.price_netto = product.quantity ? (product.unit_price_discounted * product.quantity).toFixed(2) : null;
            }
          });
        }
        const dataToEmit = this.getDataToEmit(newData);
        this.$emit("input", dataToEmit);
        this.$emit("update-form-data", dataToEmit, 2);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.contract-product-grid {
  height: calc(95vh - 290px) !important;
}
</style>
